import { IMetadataPayload } from 'components/Metadata/types';
import { INullable, TObject } from 'utils/types';

import {
  ICashierPaymentMethod,
  ICashierPaymentMethodData,
  ICashierPaymentMethodGroup,
  ICashierPaymentMethodGroupData,
  ICashierPaymentMethodLocalization,
  ICashierPMGroupItem,
} from './types';

// The cashier-payment-method API exposes metadata as flat, prefixed fields
// (`metadataExist`, `metadataEqualsValues`, …) at the top level of the entity —
// NOT a nested `metadata` object — so the grid can sort/filter on them directly.
// This map only bridges the row-editor's operator keys (`exist`, `equalsValues`,
// …) onto those wire field names, when seeding the edit form and building requests.
export const CPM_METADATA_FIELD_MAP: Record<keyof IMetadataPayload, string> = {
  equalsValues: 'metadataEqualsValues',
  exist: 'metadataExist',
  inValues: 'metadataInValues',
  notEqualsValues: 'metadataNotEqualsValues',
  notExist: 'metadataNotExist',
  notInValues: 'metadataNotInValues',
};

// Flatten the row-editor's operator-keyed payload into the prefixed fields the
// API expects. Only operators present on `metadata` are emitted (a `null` value
// clears that operator), so group edit can send just the touched operators;
// create/update pass a full payload so every operator is sent.
export const flattenCpmMetadataPayload = (
  metadata: INullable<Partial<IMetadataPayload>> | undefined,
): Record<string, INullable<string>> => {
  const result: Record<string, INullable<string>> = {};

  if (!metadata) {
    return result;
  }

  (Object.keys(CPM_METADATA_FIELD_MAP) as (keyof IMetadataPayload)[]).forEach((key) => {
    if (key in metadata) {
      result[CPM_METADATA_FIELD_MAP[key]] = metadata[key] ?? null;
    }
  });

  return result;
};

const normalizeMerchantTerminal = (
  data?: ICashierPaymentMethodData['merchantTerminal'],
): ICashierPaymentMethodGroup['merchantTerminal'] => {
  return {
    id: (data?.id as number) || 0,
    merchant: {
      enabled: !!data?.enabled,
      id: (data?.merchant?.id as number) || 0,
      name: (data?.merchant?.name as string) || '',
    },
    name: (data?.name as string) || '',
    preMemorisedRouting: !!data?.preMemorisedRouting,
  };
};

export const normalizeCashierPaymentMethod = (
  data: ICashierPaymentMethodData,
): ICashierPaymentMethod => {
  return {
    cascadeAfterTimeout: (data?.cascadeAfterTimeout as boolean) ?? null,
    cascadeRetryAttempts: (data?.cascadeRetryAttempts as number) ?? null,
    cpmGroupId: (data?.cpmGroupId as number) || 0,
    cpmGroupName: (data?.cpmGroupName as string) || '',
    defaultLabelBeforeDeposit: (data?.defaultLabelBeforeDeposit as string) || null,
    defaultLabelBeforeSubmit: (data?.defaultLabelBeforeSubmit as string) || null,
    defaultLabelUnderPaymentMethod:
      (data?.defaultLabelUnderPaymentMethod as string) || null,
    depositPriority: (data?.depositPriority as number) || null,
    displayName: (data?.displayName as string) || null,
    displayNameLocales: (data?.displayNameLocales as string) || null,
    id: (data?.id as number) || 0,
    isCashierPaymentMethod: !!data?.isCashierPaymentMethod,
    labelBeforeDepositEnabled: !!data?.labelBeforeDepositEnabled,
    labelBeforeDepositLocales: (data?.labelBeforeDepositLocales as string) || null,
    labelBeforeSubmitEnabled: !!data?.labelBeforeSubmitEnabled,
    labelBeforeSubmitLocales: (data?.labelBeforeSubmitLocales as string) || null,
    labelUnderPaymentMethodEnabled: !!data?.labelUnderPaymentMethodEnabled,
    labelUnderPaymentMethodLocales:
      (data?.labelUnderPaymentMethodLocales as string) || null,
    logoData: (data?.logoData as string) || '',
    logoFileName: (data?.logoFileName as string) || '',
    merchantId: (data?.merchantId as number) || 0,
    merchantName: (data?.merchantName as string) || '',
    merchantTerminal: normalizeMerchantTerminal(data?.merchantTerminal),
    merchantTerminalId: (data?.merchantTerminalId as number) || 0,
    merchantTerminalName: (data?.merchantTerminalName as string) || '',
    metadataEqualsValues: (data?.metadataEqualsValues as string) || null,
    metadataExist: (data?.metadataExist as string) || null,
    metadataInValues: (data?.metadataInValues as string) || null,
    metadataNotEqualsValues: (data?.metadataNotEqualsValues as string) || null,
    metadataNotExist: (data?.metadataNotExist as string) || null,
    metadataNotInValues: (data?.metadataNotInValues as string) || null,
    name: (data?.name as string) || '',
    paymentInstrument: (data?.paymentInstrument as string) || '',
    paymentMethod: (data?.paymentMethod as string) || '',
    usePreMemorisedRouting: !!data?.usePreMemorisedRouting,
    withdrawalPriority: (data?.withdrawalPriority as number) || null,
  };
};

export const normalizeCashierPaymentMethodLocalization = (
  data: TObject & { cashierPaymentMethod: TObject },
): ICashierPaymentMethodLocalization => {
  return {
    cashierPaymentMethod: {
      name: (data?.cashierPaymentMethod?.name as string) || '',
      paymentMethod: (data?.cashierPaymentMethod?.paymentMethod as string) || '',
    },
    fieldName: (data?.fieldName as string) || '',
    id: (data?.id as number) || 0,
    locale: (data?.locale as string) || '',
    localValue: (data?.localValue as string) || '',
  };
};

const normalizeCashierPMGroupItem = (
  data: ICashierPaymentMethodData,
): ICashierPMGroupItem => {
  return {
    name: (data?.name as string) || '',
    paymentMethod: (data?.paymentMethod as string) || '',
  };
};

export const normalizeCashierPaymentMethodGroup = (
  data: ICashierPaymentMethodGroupData,
): ICashierPaymentMethodGroup => {
  let normalizedCashierPaymentMethods: ICashierPMGroupItem[] = [];

  if (data?.cashierPaymentMethods && Array.isArray(data.cashierPaymentMethods)) {
    normalizedCashierPaymentMethods = data.cashierPaymentMethods.map((cashierPM) =>
      normalizeCashierPMGroupItem(cashierPM),
    );
  }

  return {
    cashierPaymentMethods: normalizedCashierPaymentMethods,
    depositPriority: (data?.depositPriority as number) || null,
    groupName: (data?.groupName as string) || '',
    id: (data?.id as number) || 0,
    logoData: (data?.logoData as string) || '',
    logoFileName: (data?.logoFileName as string) || null,
    merchantTerminal: normalizeMerchantTerminal(data?.merchantTerminal),
    withdrawalPriority: (data?.withdrawalPriority as number) || null,
  };
};
